/* CookieConsentBanner — compact, non-blocking lower-screen banner.
 *
 * Plan §"Cookie Consent & Session Preference":
 *   - Primary actions (exact labels):
 *     * `Přijmout všechny`
 *     * `Přijmout vybrané`
 *     * `Přijmout povinné`
 *   - `Přijmout vybrané` panel categories: Functional, Analytics, Marketing.
 *   - Necessary is always active and non-editable.
 *   - Reopenable via `Nastavení cookies` footer trigger (handled by the
 *     `openBanner` entry point on the context).
 *
 * Bottom offset: `--customer-toast-offset` (BottomNav + sticky-cart clearance)
 * so the banner sits above StickyCartBar at md/tablet — not coplanar on chrome.
 *
 * G17 — outer region is `pointer-events-none`; only the Card is
 * `pointer-events-auto` (ToastHost / sticky-cart-slot pattern). A tall fixed
 * shell must not intercept mid-page Filtry / Orders / sign-in CTAs.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOMER_COOKIE_BANNER_WIDTH_CLASS,
  CUSTOMER_SHELL_WIDTH_CLASS,
} from '../../app/customerTopBarStyles.js';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../app/pwa/customerFormDirtyGate.js';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useCookieConsent } from './CookieConsentProvider.js';
import { useMarketingAnalyticsConsentPrompt } from './consentApi.js';

export function CookieConsentBanner(): JSX.Element | null {
  const consent = useCookieConsent();
  const { session, sessionHydrated } = useCustomerSession();
  const promptQuery = useMarketingAnalyticsConsentPrompt();
  // Cookie CMP first: only suppress the banner for the logged-in M+A prompt
  // after a local cookie decision already exists (returning / accept-all path).
  // First-visit (decision === null) must show the cookie banner, not the account modal.
  const marketingPromptBlocksBanner =
    sessionHydrated &&
    session !== null &&
    consent.decision !== null &&
    !promptQuery.isLoading &&
    promptQuery.data?.needsPrompt === true;

  if (!consent.isBannerOpen || marketingPromptBlocksBanner) {
    return null;
  }

  // Remount when reopen options change so edit-consent preselect + panel expand
  // initialize correctly without setState-in-effect.
  return (
    <CookieConsentBannerPanel
      key={`${String(consent.bannerPreselectFunctional)}-${String(consent.decision?.decidedAtMs ?? 'none')}`}
    />
  );
}

function CookieConsentBannerPanel(): JSX.Element {
  const { t } = useTranslation('customer');
  const consent = useCookieConsent();
  const preselectFunctional = consent.bannerPreselectFunctional;
  const [showPanel, setShowPanel] = useState(preselectFunctional);
  const initialFunctional =
    preselectFunctional || (consent.decision?.functional ?? false);
  const [functional, setFunctional] = useState<boolean>(initialFunctional);
  const [analytics, setAnalytics] = useState<boolean>(
    consent.decision?.analytics ?? false
  );
  const [marketing, setMarketing] = useState<boolean>(
    consent.decision?.marketing ?? false
  );
  const [profiling, setProfiling] = useState<boolean>(
    consent.decision?.profiling ?? false
  );

  const isDraftDirty =
    showPanel ||
    functional !== initialFunctional ||
    analytics !== (consent.decision?.analytics ?? false) ||
    marketing !== (consent.decision?.marketing ?? false) ||
    profiling !== (consent.decision?.profiling ?? false);

  return (
    <div
      className={`customer-safe-area-x pointer-events-none fixed inset-x-0 bottom-[var(--customer-toast-offset,0px)] z-50 px-3 pb-2 pt-3 ${CUSTOMER_SHELL_WIDTH_CLASS} lg:[html[data-sidebar-visible]_&]:pl-56 lg:bottom-0`}
      role="region"
      aria-label={t('consent.regionLabel')}
      data-testid="cookie-consent-banner"
      {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
    >
      <Card
        padded
        className={`pointer-events-auto ${CUSTOMER_COOKIE_BANNER_WIDTH_CLASS} max-h-[min(42svh,17.5rem)] overflow-y-auto border border-[var(--color-border)] shadow-[var(--shadow-popover)]`}
      >
        <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
          {t('consent.title')}
        </h2>
        <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
          {t('consent.description')}
        </p>
        <p className="mt-2 text-xs text-[var(--color-on-surface-muted)]">
          {t('consent.crossTenantNotice')}
        </p>

        {showPanel ? (
          <fieldset className="mt-3 flex flex-col gap-2 border-t border-[var(--color-border)] pt-3">
            <legend className="sr-only">{t('consent.categoriesLegend')}</legend>
            <CategoryRow
              id="consent-necessary"
              label={t('consent.necessary')}
              description={t('consent.necessaryDescription')}
              checked
              disabled
            />
            <CategoryRow
              id="consent-functional"
              label={t('consent.functional')}
              description={t('consent.functionalDescription')}
              checked={functional}
              onChange={setFunctional}
            />
            <CategoryRow
              id="consent-analytics"
              label={t('consent.analytics')}
              description={t('consent.analyticsDescription')}
              checked={analytics}
              onChange={setAnalytics}
            />
            <CategoryRow
              id="consent-marketing"
              label={t('consent.marketing')}
              description={t('consent.marketingDescription')}
              checked={marketing}
              onChange={setMarketing}
            />
            {(() => {
              if (!consent.isLoggedIn) {
                return null;
              }
              if (consent.profilingLoading) {
                return (
                  <CategoryRow
                    id="consent-profiling"
                    label={t('loyalty.profilingTitle')}
                    description={t('loyalty.profilingHelp')}
                    checked={profiling}
                    disabled
                    loading
                  />
                );
              }
              if (!consent.loyaltyProfilingAvailable) {
                return null;
              }
              return (
                <CategoryRow
                  id="consent-profiling"
                  label={t('loyalty.profilingTitle')}
                  description={t('loyalty.profilingHelp')}
                  checked={profiling}
                  onChange={setProfiling}
                />
              );
            })()}
          </fieldset>
        ) : null}

        <div className="mt-3 flex flex-col gap-2 min-[30rem]:flex-row min-[30rem]:items-stretch">
          <Button
            intent="primary"
            size="md"
            data-intent="primary"
            className="w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1"
            onClick={consent.acceptAll}
          >
            {t('consent.acceptAll')}
          </Button>
          {showPanel ? (
            <Button
              intent="secondary"
              size="md"
              data-intent="secondary"
              className="w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1"
              onClick={() => {
                consent.acceptSelection({
                  functional,
                  analytics,
                  marketing,
                  profiling:
                    consent.isLoggedIn && consent.loyaltyProfilingAvailable
                      ? profiling
                      : false,
                });
              }}
            >
              {t('consent.acceptSelected')}
            </Button>
          ) : (
            <Button
              intent="secondary"
              size="md"
              data-intent="secondary"
              className="w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1"
              onClick={() => {
                const d = consent.decision;
                if (d !== null) {
                  setFunctional(preselectFunctional ? true : d.functional);
                  setAnalytics(d.analytics);
                  setMarketing(d.marketing);
                  setProfiling(d.profiling);
                } else {
                  setFunctional(preselectFunctional);
                  setAnalytics(false);
                  setMarketing(false);
                  setProfiling(false);
                }
                setShowPanel(true);
              }}
            >
              {t('consent.acceptSelected')}
            </Button>
          )}
          <Button
            intent="secondary"
            size="md"
            data-intent="secondary"
            className="w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1"
            onClick={consent.acceptNecessary}
          >
            {t('consent.rejectNonEssential')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

interface CategoryRowProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  loading?: boolean;
  onChange?: (next: boolean) => void;
}

function CategoryRow({
  id,
  label,
  description,
  checked,
  disabled,
  loading,
  onChange,
}: CategoryRowProps): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        className="mt-1 h-4 w-4 accent-[var(--color-accent)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          if (onChange !== undefined) {
            onChange(e.target.checked);
          }
        }}
      />
      <label htmlFor={id} className="flex flex-col">
        <span className="text-sm font-medium text-[var(--color-on-surface)]">
          {label}
        </span>
        <span className="text-xs text-[var(--color-on-surface-muted)]">
          {description}
        </span>
        {loading ? (
          <span className="text-xs text-[var(--color-on-surface-muted)]">
            ...
          </span>
        ) : null}
      </label>
    </div>
  );
}
